import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { FlaskConical, Pencil, Plus, Trash2 } from 'lucide-react';
import { AcaoPrimaria } from '../../componentes/acao-primaria';
import { Botao, Cartao, Selo, type CorSelo } from '../../componentes/base';
import { ConfirmarExclusao } from '../../componentes/confirmar-exclusao';
import { Estado, Vazio } from '../../componentes/estados';
import { Formulario, type DefCampo } from '../../componentes/formulario';
import { Drawer, Modal } from '../../componentes/sobreposicao';
import { useToast } from '../../componentes/toast';
import { get, mensagemDeErro, patch, post } from '../../nucleo/api';
import { data, numero } from '../../nucleo/formato';
import { useSessao } from '../../nucleo/sessao';
import type { Lista, TenantPlataforma, Workspace } from './tipos';

const COR_ESTADO: Record<TenantPlataforma['estado'], CorSelo> = { ativo: 'verde', suspenso: 'laranja', cancelado: 'perigo' };

/** M12.3: workspaces do tenant atual (listar, criar, renomear, marcar sandbox). */
function Workspaces({ criando, aoFecharCriar }: { criando: boolean; aoFecharCriar: () => void }) {
  const { t } = useTranslation('admin');
  const { t: tn } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const [editando, setEditando] = useState<Workspace | null>(null);
  // Item 5 (21/10): excluir é arquivar, com o nome digitado (a API confere Dono de produção e as travas).
  const [excluindo, setExcluindo] = useState<Workspace | null>(null);
  const [enviando, setEnviando] = useState(false);
  const q = useQuery({ queryKey: ['/admin/workspaces'], queryFn: () => get<Lista<Workspace>>('/admin/workspaces') });
  const recarregar = async () => {
    await qc.invalidateQueries({ queryKey: ['/admin/workspaces'] });
    await qc.invalidateQueries({ queryKey: ['sessao'] });
  };

  const camposNovo: DefCampo[] = [
    { nome: 'nome', rotulo: t('tenants.nome'), tipo: 'texto', obrigatorio: true },
    { nome: 'slug', rotulo: t('tenants.slug'), tipo: 'texto', ajuda: t('tenants.slugAjuda') },
    { nome: 'sandbox', rotulo: t('tenants.sandbox'), tipo: 'booleano', ajuda: t('tenants.sandboxAjuda') },
  ];
  const camposEditar: DefCampo[] = [
    { nome: 'nome', rotulo: t('tenants.nome'), tipo: 'texto', obrigatorio: true },
    { nome: 'sandbox', rotulo: t('tenants.sandbox'), tipo: 'booleano', ajuda: t('tenants.sandboxAjuda') },
  ];

  return (
    <Cartao titulo={t('tenants.workspaces')}>
      <Estado consulta={q}>
        {(d) =>
          d.itens.length === 0 ? (
            <Vazio texto={t('tenants.vazioWorkspaces')} />
          ) : (
            <ul className="divide-y divide-borda">
              {d.itens.map((w) => (
                <li key={w.id} className="flex flex-wrap items-center justify-between gap-3 py-3">
                  {/* Review r12 robustez B4: slug longo sem espaço (60 caracteres) quebra em qualquer ponto; a linha não passa da tela a 390. */}
                  <div className="min-w-0 max-w-full">
                    <p className="flex flex-wrap items-center gap-2 font-medium">
                      <span className="min-w-0 max-w-full truncate" title={w.nome}>{w.nome}</span>
                      {w.atual && <Selo cor="acento">{t('tenants.atual')}</Selo>}
                      {w.sandbox && (
                        <Selo cor="roxo">
                          <FlaskConical className="mr-1 size-3" aria-hidden />
                          {t('tenants.sandbox')}
                        </Selo>
                      )}
                    </p>
                    <p className="numero mt-0.5 min-w-0 text-xs text-tinta-3 wrap-anywhere">
                      {w.slug} · {t('tenants.membros', { count: w.membros })} · {data(w.created_at)}
                    </p>
                  </div>
                  <Botao tamanho="sm" icone={<Pencil className="size-4" />} onClick={() => setEditando(w)}>
                    {tn('comum.editar')}
                  </Botao>
                </li>
              ))}
            </ul>
          )
        }
      </Estado>

      <Modal
        aberto={criando}
        aoFechar={aoFecharCriar}
        titulo={t('tenants.novoWorkspace')}
        rodape={
          <>
            <Botao variante="fantasma" onClick={aoFecharCriar}>
              {tn('comum.cancelar')}
            </Botao>
            <Botao variante="primario" type="submit" form="form-ws-novo">
              {tn('comum.criar')}
            </Botao>
          </>
        }
      >
        <Formulario
          id="form-ws-novo"
          campos={camposNovo}
          inicial={{ sandbox: false }}
          aoEnviar={async (v) => {
            await post('/admin/workspaces', { nome: v.nome, slug: v.slug || undefined, sandbox: !!v.sandbox });
            await recarregar();
            toast.sucesso(t('tenants.workspaceCriado'));
            aoFecharCriar();
          }}
        />
      </Modal>

      <Modal
        aberto={!!editando}
        aoFechar={() => setEditando(null)}
        titulo={t('tenants.editarWorkspace')}
        rodape={
          <>
            {editando && !editando.atual && (
              <Botao
                variante="perigo"
                className="mr-auto"
                icone={<Trash2 className="size-4" />}
                onClick={() => {
                  setExcluindo(editando);
                  setEditando(null);
                }}
              >
                {tn('exclusao.estrutura.excluir')}
              </Botao>
            )}
            <Botao variante="fantasma" onClick={() => setEditando(null)}>
              {tn('comum.cancelar')}
            </Botao>
            <Botao variante="primario" type="submit" form="form-ws-editar">
              {tn('comum.salvar')}
            </Botao>
          </>
        }
      >
        {editando && (
          <Formulario
            key={editando.id}
            id="form-ws-editar"
            campos={camposEditar}
            inicial={{ nome: editando.nome, sandbox: editando.sandbox }}
            aoEnviar={async (v) => {
              await patch(`/admin/workspaces/${editando.id}`, { nome: v.nome, sandbox: !!v.sandbox });
              await recarregar();
              toast.sucesso(tn('comum.salvo'));
              setEditando(null);
            }}
          />
        )}
      </Modal>
      <ConfirmarExclusao
        aberto={!!excluindo}
        aoFechar={() => setExcluindo(null)}
        titulo={t('tenants.excluirWorkspace', { nome: excluindo?.nome })}
        texto={t('tenants.excluirWorkspaceTexto')}
        nome={excluindo?.nome ?? ''}
        carregando={enviando}
        aoConfirmar={async ({ confirmacao }) => {
          if (!excluindo) return;
          setEnviando(true);
          try {
            await post(`/admin/workspaces/${excluindo.id}/arquivar`, { confirmacao });
            await recarregar();
            toast.sucesso(tn('exclusao.estrutura.feito'));
            setExcluindo(null);
          } catch (e) {
            toast.erro(mensagemDeErro(e));
          } finally {
            setEnviando(false);
          }
        }}
      />
    </Cartao>
  );
}

/** M12.3 (plataforma): todos os tenants, criação com convite ao dono, plano, créditos e estado. */
function Plataforma({ criando, aoFecharCriar }: { criando: boolean; aoFecharCriar: () => void }) {
  const { t } = useTranslation('admin');
  const { t: tn } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const [editando, setEditando] = useState<TenantPlataforma | null>(null);
  // Item 5 (21/10): excluir é cancelar e arquivar, com o nome digitado; nunca o próprio tenant.
  const [excluindo, setExcluindo] = useState<TenantPlataforma | null>(null);
  const [enviando, setEnviando] = useState(false);
  const proprio = useSessao().workspace.tenant_id;
  const q = useQuery({ queryKey: ['/admin/plataforma/tenants'], queryFn: () => get<Lista<TenantPlataforma>>('/admin/plataforma/tenants') });
  const recarregar = () => qc.invalidateQueries({ queryKey: ['/admin/plataforma/tenants'] });
  const estados = (['ativo', 'suspenso', 'cancelado'] as const).map((e) => ({ valor: e, rotulo: t(`tenants.estados.${e}`) }));

  const camposNovo: DefCampo[] = [
    { nome: 'nome', rotulo: t('tenants.tenant'), tipo: 'texto', obrigatorio: true },
    { nome: 'workspace_nome', rotulo: t('tenants.workspaceInicial'), tipo: 'texto', largura: 'meia' },
    { nome: 'plano', rotulo: t('tenants.plano'), tipo: 'texto', largura: 'meia' },
    { nome: 'dono_nome', rotulo: t('tenants.donoNome'), tipo: 'texto', obrigatorio: true, largura: 'meia' },
    { nome: 'dono_email', rotulo: t('tenants.donoEmail'), tipo: 'email', obrigatorio: true, largura: 'meia' },
  ];
  const camposEditar: DefCampo[] = [
    { nome: 'plano', rotulo: t('tenants.plano'), tipo: 'texto', obrigatorio: true, largura: 'meia' },
    { nome: 'estado', rotulo: t('tenants.estado'), tipo: 'selecao', obrigatorio: true, opcoes: estados, largura: 'meia' },
    { nome: 'creditos', rotulo: t('tenants.creditos'), tipo: 'numero', min: 0, passo: 1, obrigatorio: true },
  ];

  return (
    <Cartao titulo={t('tenants.plataforma')}>
      <p className="mb-4 text-sm text-tinta-3">{t('tenants.plataformaTexto')}</p>
      <Estado consulta={q}>
        {(d) =>
          d.itens.length === 0 ? (
            <Vazio texto={t('tenants.vazioTenants')} />
          ) : (
            <>
              <div className="hidden overflow-x-auto md:block">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-borda text-left text-tinta-3">
                      <th scope="col" className="px-3 py-2 font-medium">{t('tenants.tenant')}</th>
                      <th scope="col" className="px-3 py-2 font-medium">{t('tenants.plano')}</th>
                      <th scope="col" className="px-3 py-2 font-medium">{t('tenants.estado')}</th>
                      <th scope="col" className="px-3 py-2 text-right font-medium">{t('tenants.creditos')}</th>
                      <th scope="col" className="px-3 py-2 text-right font-medium">{t('tenants.qtdWorkspaces')}</th>
                      <th scope="col" className="px-3 py-2 text-right font-medium">{t('tenants.qtdUsuarios')}</th>
                      <th scope="col" className="px-3 py-2 font-medium">{t('tenants.criadoEm')}</th>
                      <th scope="col" className="relative px-3 py-2"><span className="sr-only">{tn('comum.acoes')}</span></th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.itens.map((x) => (
                      <tr key={x.id} className="border-b border-borda last:border-0">
                        <td className="px-3 py-3 font-medium">{x.nome}</td>
                        <td className="px-3 py-3">{x.plano}</td>
                        <td className="px-3 py-3"><Selo cor={COR_ESTADO[x.estado]}>{t(`tenants.estados.${x.estado}`)}</Selo></td>
                        <td className="numero px-3 py-3 text-right">{numero(x.creditos)}</td>
                        <td className="numero px-3 py-3 text-right">{numero(x.workspaces)}</td>
                        <td className="numero px-3 py-3 text-right">{numero(x.usuarios)}</td>
                        <td className="numero px-3 py-3 whitespace-nowrap">{data(x.created_at)}</td>
                        <td className="px-3 py-3 text-right">
                          <Botao tamanho="sm" aria-label={t('tenants.editarTenant', { nome: x.nome })} icone={<Pencil className="size-4" />} onClick={() => setEditando(x)} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <ul className="divide-y divide-borda md:hidden">
                {d.itens.map((x) => (
                  <li key={x.id} className="flex flex-col gap-1.5 py-3">
                    <span className="flex items-center justify-between gap-2">
                      <span className="truncate font-medium" title={x.nome}>{x.nome}</span>
                      <Selo cor={COR_ESTADO[x.estado]}>{t(`tenants.estados.${x.estado}`)}</Selo>
                    </span>
                    <span className="text-xs text-tinta-3">
                      {x.plano} · {t('tenants.creditosQtd', { count: x.creditos, valor: numero(x.creditos) })} · {t('tenants.qtdWorkspaces')} {x.workspaces} · {t('tenants.qtdUsuarios')} {x.usuarios}
                    </span>
                    <Botao tamanho="sm" className="self-start" icone={<Pencil className="size-4" />} onClick={() => setEditando(x)}>
                      {tn('comum.editar')}
                    </Botao>
                  </li>
                ))}
              </ul>
            </>
          )
        }
      </Estado>

      <Drawer
        aberto={criando}
        aoFechar={aoFecharCriar}
        titulo={t('tenants.novoTenant')}
        rodape={
          <>
            <Botao variante="fantasma" onClick={aoFecharCriar}>
              {tn('comum.cancelar')}
            </Botao>
            <Botao variante="primario" type="submit" form="form-tenant-novo">
              {tn('comum.criar')}
            </Botao>
          </>
        }
      >
        <p className="mb-4 rounded-lg border border-borda bg-sup-2 px-3 py-2 text-sm text-tinta-2">{t('tenants.conviteAviso')}</p>
        <Formulario
          id="form-tenant-novo"
          campos={camposNovo}
          inicial={{ workspace_nome: 'Principal', plano: 'padrao' }}
          aoEnviar={async (v) => {
            await post('/admin/plataforma/tenants', {
              nome: v.nome,
              workspace_nome: v.workspace_nome || undefined,
              plano: v.plano || undefined,
              dono_nome: v.dono_nome,
              dono_email: v.dono_email,
            });
            await recarregar();
            toast.sucesso(t('tenants.tenantCriado'));
            aoFecharCriar();
          }}
        />
      </Drawer>

      <Modal
        aberto={!!editando}
        aoFechar={() => setEditando(null)}
        titulo={t('tenants.editarTenant', { nome: editando?.nome })}
        rodape={
          <>
            {editando && editando.id !== proprio && (
              <Botao
                variante="perigo"
                className="mr-auto"
                icone={<Trash2 className="size-4" />}
                onClick={() => {
                  setExcluindo(editando);
                  setEditando(null);
                }}
              >
                {tn('exclusao.estrutura.excluir')}
              </Botao>
            )}
            <Botao variante="fantasma" onClick={() => setEditando(null)}>
              {tn('comum.cancelar')}
            </Botao>
            <Botao variante="primario" type="submit" form="form-tenant-editar">
              {tn('comum.salvar')}
            </Botao>
          </>
        }
      >
        {editando && (
          <Formulario
            key={editando.id}
            id="form-tenant-editar"
            campos={camposEditar}
            inicial={{ plano: editando.plano, estado: editando.estado, creditos: editando.creditos }}
            aoEnviar={async (v) => {
              await patch(`/admin/plataforma/tenants/${editando.id}`, { plano: v.plano, estado: v.estado, creditos: Number(v.creditos ?? 0) });
              await recarregar();
              toast.sucesso(tn('comum.salvo'));
              setEditando(null);
            }}
          />
        )}
      </Modal>
      <ConfirmarExclusao
        aberto={!!excluindo}
        aoFechar={() => setExcluindo(null)}
        titulo={t('tenants.excluirTenant', { nome: excluindo?.nome })}
        texto={t('tenants.excluirTenantTexto')}
        nome={excluindo?.nome ?? ''}
        carregando={enviando}
        aoConfirmar={async ({ confirmacao }) => {
          if (!excluindo) return;
          setEnviando(true);
          try {
            await post(`/admin/plataforma/tenants/${excluindo.id}/arquivar`, { confirmacao });
            await recarregar();
            toast.sucesso(tn('exclusao.estrutura.feito'));
            setExcluindo(null);
          } catch (e) {
            toast.erro(mensagemDeErro(e));
          } finally {
            setEnviando(false);
          }
        }}
      />
    </Cartao>
  );
}

export function Tenants() {
  const { t } = useTranslation('admin');
  const s = useSessao();
  const [novoWs, setNovoWs] = useState(false);
  const [novoTenant, setNovoTenant] = useState(false);
  const plataforma = s.usuario.plataforma_admin;
  return (
    <>
      <AcaoPrimaria>
        {plataforma && (
          <Botao icone={<Plus className="size-4" />} onClick={() => setNovoTenant(true)}>
            {t('tenants.novoTenant')}
          </Botao>
        )}
        <Botao variante="primario" icone={<Plus className="size-4" />} onClick={() => setNovoWs(true)}>
          {t('tenants.novoWorkspace')}
        </Botao>
      </AcaoPrimaria>
      <div className="flex flex-col gap-5">
        <Workspaces criando={novoWs} aoFecharCriar={() => setNovoWs(false)} />
        {plataforma && <Plataforma criando={novoTenant} aoFecharCriar={() => setNovoTenant(false)} />}
      </div>
    </>
  );
}
